// --- Core Dependencies
import React from 'react';
import {useSelector} from 'react-redux';
// --- UI Components
import {SharedSectionModal} from '../modals/create-edit';
import {LinkedProductsModal} from '../modals/linked-products';
// --- Hooks
import {useAgGridApi} from '../../../../components/AGGrid/hooks';
import {useLoading} from '../../../../utils/hooks/useLoading';
// --- API and State Management
import {datastageApi} from '../../../../api';
import {useSharedSectionsActions} from '../../../../store/sharedSections/useSharedSections';
import {useLinkedProductsActions} from '../../../../store/linkedProducts/useProducts';
import {useSharedSectionsGridUtils} from "./utils/gridUtils";
import DataGrid from "@dtci/spellbook/DataGrid";
import styles from "../../../../components/AGGrid/details-table.module.scss";
import {DetailsRow} from "../../../../components/AGGrid/details-row/details-row";

/**
 * SharedSectionsGrid Component
 * Renders a grid displaying shared sections and provides modals for editing and linking products.
 *
 * @returns {JSX.Element} The Shared Sections Grid component.
 */
export const SharedSectionsGrid = () => {
    const agGridApi = useAgGridApi();
    const {setLoadingTrue, setLoadingFalse} = useLoading();
    const {shared, filterForm, defaultFilterRange} = useSelector((state) => state.sharedSections);
    const {rangeParam} = useSelector((state) => state.linkedProducts);
    const {updateSharedSections} = useSharedSectionsActions();
    const {updateLinkedProducts} = useLinkedProductsActions();

    const {
        setSort,
        columns
    } = useSharedSectionsGridUtils({
        datastageApi,
        filterForm,
        defaultFilterRange,
        updateSharedSections,
        handlers: {
            setLoadingTrue,
            setLoadingFalse,
            columnHandlers: [
                (data) => () => openSharedSectionModal(data),
                (data) => () => openLinkedProductsModal(data),
            ]
        }
    });

    // Fetch tags for a specific section
    const fetchTags = async (data) => {
        if (!data?.tagIdList?.length) return {clearanceMemoTags: []};
        try {
            const tags = await datastageApi.clearanceMemoTags(data.tagIdList);
            return {
                clearanceMemoTags:
                    tags?.content?.map(({tagName, id}) => ({label: tagName, value: id})) ?? [],
            };
        } catch (error) {
            console.error('Error fetching tags:', error);
            return {clearanceMemoTags: []};
        }
    };

    // Fetch products for a specific section
    const fetchProducts = async (data) => {
        try {
            // Run both product fetches in parallel
            const [linkedProductsData, productData] = await Promise.all([
                datastageApi.products(
                    {sectionIdForProductsToInclude: data?.id},
                    rangeParam,
                    ['id', 'ASC'],
                ),
                datastageApi.products(
                    {sectionIdForProductsToExclude: data?.id, hasClearanceMemo: true},
                    [0, 24],
                    ['productTitle', 'ASC'],
                ),
            ]);

            const linkedProducts = (linkedProductsData?.content ?? []).map((product) => ({
                ...product,
                addedDate: data?.sharedClearanceMemoTOCProductAddedMap?.[product.productId] || '',
                verified: data?.unConfirmedProductIdList?.[product.productId] ? 'No' : 'Yes',
                isLinked: true,
            }));

            const productOptions = (productData?.content ?? []).map(({productTitle, id}) => ({
                label: productTitle,
                value: id,
            }));

            updateLinkedProducts({
                totalPages: linkedProductsData?.totalPages,
                currentPage: linkedProductsData?.pageable?.pageNumber,
                totalItems: linkedProductsData?.totalElements,
            });

            return {
                linkedProducts,
                productOptions,
                dateAddedMap: data?.sharedClearanceMemoTOCProductAddedMap ?? {},
                unconfirmedProductIdList: data?.unConfirmedProductIdList ?? {},
            };
        } catch (error) {
            console.error('Error fetching products:', error);
            return {
                linkedProducts: [],
                productOptions: [],
                dateAddedMap: {},
                unconfirmedProductIdList: {},
            };
        }
    };
    // Open the shared section modal for creating/editing sections
    const openSharedSectionModal = async (data) => {
        try {
            setLoadingTrue();
            const {clearanceMemoTags} = await fetchTags(data);
            updateSharedSections({
                isSSModalOpen: true,
                modalMode: 'edit',
                clearanceMemoTags,
                section: data,
            });
        } catch (error) {
            console.error('Error opening shared section modal:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Open the linked products modal for a section
    const openLinkedProductsModal = async (data) => {
        try {
            setLoadingTrue();

            const {linkedProducts, productOptions, dateAddedMap, unconfirmedProductIdList} = await fetchProducts(
                data,
            );

            updateSharedSections({section: data});
            updateLinkedProducts({
                isProductsModalOpen: true,
                linkedProducts,
                dateAddedMap,
                unconfirmedProductIdList,
                productOptions
            });
        } catch (error) {
            console.error('Error opening linked products modal:', error);
        } finally {
            setLoadingFalse();
        }
    };

    return (
        <>
            {/* Grid displaying shared sections */}
            <DataGrid
                className={styles.dataGrid}
                autoHeight
                autoSizing
                agGrid={{
                    ref: agGridApi.AgGridApiRef,
                    rowData: shared,
                    headerHeight: 45,
                    detailCellRenderer: (params) => <DetailsRow dataType="sharedSections" data={params.data} />,
                    detailRowAutoHeight: true,
                    masterDetail: true,
                }}
                bordered="none"
                columnDefs={columns}
                sortable={false}
                striped
                noResultsMessage={'No Shared Sections'}
            />
            {/* Modal for creating or editing shared sections */}
            <SharedSectionModal/>
            {/* Modal for managing linked products */}
            <LinkedProductsModal/>
        </>
    );
};
